import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Button,
  Card,
  Divider,
  Heading,
  MarkdownLite,
  Stack,
  Text,
  TextField,
} from "@origin-digital/ods-core";
import { errorForm, logger } from "@origin-digital/reporting-client";
import { useAppContext } from "../../context/AppContext";
import { postRequestCallback } from "../../services/lpgOffers";
import { APP_NAME, Routes } from "../../consts";
import { showErrorNotification } from "../../utils/notifications";
import {
  validateEmail,
  validateName,
  validatePayload,
  validatePhone,
} from "../../utils/validator";
import content from "./content.json";
import {
  ActionType,
  LPG_FUEL,
  LPGForm,
  SelfServiceActionType,
  submitInteractionEvent,
  submitTaskAnalyticsEvent,
  TaskType,
} from "../../utils/analytics";
import { CaptchaDisclaimer } from "../CaptchaDisclaimer/CaptchaDisclaimer";
import { getCaptchaV3Token } from "../../services/CaptchaService";
import { getGoogleCaptchaKey } from "../../config";
import { IRequestCallback } from "../../model/IRequestCallback";

export const CallbackForm: React.FC = () => {
  const { context } = useAppContext();
  const history = useHistory();

  const [firstName, setFirstName] = useState("");
  const [firstNameError, setFirstNameError] = useState<string | undefined>("");

  const [lastName, setLastName] = useState("");
  const [lastNameError, setLastNameError] = useState<string | undefined>("");

  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState<string | undefined>("");

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | undefined>("");

  const [submittingForm, setSubmittingForm] = useState<boolean>(false);

  const formState = {
    formStarted: false,
  };

  const handleValidation = () => {
    const errors = validatePayload({
      firstName,
      lastName,
      email,
      phone,
    });
    setFirstNameError(errors.firstNameError || undefined);
    setLastNameError(errors.lastNameError || undefined);
    setEmailError(errors.emailError || undefined);
    setPhoneError(errors.phoneError || undefined);

    logger.debug("[handleValidation]", errors);

    return (
      !errors.firstNameError &&
      !errors.lastNameError &&
      !errors.emailError &&
      !errors.phoneError
    );
  };

  const constructCallbackRequest = (token: string) => {
    const request: IRequestCallback = {
      firstName,
      lastName,
      mobile: phone,
      email,
      suburb: context.lookupAddress?.qasAddress.city ?? "",
      postcode: context.lookupAddress?.qasAddress.postcode ?? "",
      streetName: context.lookupAddress?.qasAddress.streetName ?? "",
      streetNumber: context.lookupAddress?.qasAddress.streetNumber ?? "",
      unitType: context.lookupAddress?.qasAddress.unitType,
      unitNumber: context.lookupAddress?.qasAddress.unit,
      state: context.lookupAddress?.qasAddress.state ?? "",
      isRefill: context.selectedOffer?.isRefill ?? false,
      salesforceId: context.selectedOffer?.salesforceId ?? "",
      productQuantity: context.selectedOffer?.productQuantity ?? 0,
      price: context.selectedOffer?.bottlePricing?.["1"].price ?? 0,
      recaptchaToken: token,
    };

    return request;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (handleValidation()) {
      setSubmittingForm(true);
      submitInteractionEvent(
        "click",
        "button",
        "submit-callback",
        APP_NAME,
        `Request a call back:${context.selectedOffer?.offerName}`
      );
      const captchaToken = await getCaptchaV3Token(getGoogleCaptchaKey());
      postRequestCallback(constructCallbackRequest(captchaToken)).then(
        () => {
          // nav to thank you page
          setSubmittingForm(false);
          submitTaskAnalyticsEvent(
            TaskType.LPG_RESI_CALLBACK,
            ActionType.COMPLETE,
            LPGForm.REQUEST_CALLBACK,
            SelfServiceActionType.RESI_CALLBACK,
            context.lookupAddress?.qasAddress.state,
            context.lookupAddress?.qasAddress.postcode,
            {
              fuelType: LPG_FUEL,
              title: `${context.selectedOffer?.offerName}:${
                context.selectedOffer?.isRefill ? "refill" : "exchange"
              }`,
            }
          );
          history.push(Routes.THANK_YOU);
        },
        (err) => {
          setSubmittingForm(false);
          showErrorNotification(content.errors.submitRequest);
          errorForm({
            appName: APP_NAME,
            errorCode: err.code,
            errorMessage: content.errors.submitRequest,
            formName: "lpg-request-callback-form",
          });
          logger.error("[CallbackForm.postRequestCallback] Fetch error", err);
        }
      );
    } else {
      logger.debug("[handleSubmit] Validation error");
    }
  };

  if (!formState.formStarted) {
    submitTaskAnalyticsEvent(
      TaskType.LPG_RESI_CALLBACK,
      ActionType.START,
      LPGForm.REQUEST_CALLBACK,
      SelfServiceActionType.RESI_CALLBACK,
      context.lookupAddress?.qasAddress.state,
      context.lookupAddress?.qasAddress.postcode,
      {
        fuelType: LPG_FUEL,
        title: `${context.selectedOffer?.offerName}:${
          context.selectedOffer?.isRefill ? "refill" : "exchange"
        }`,
      }
    );
    formState.formStarted = true;
  }

  return (
    <Stack data-id="callback-form" space={["xsmall", "large"]}>
      <form onSubmit={handleSubmit} data-id="callback-form">
        <Card padding="large">
          <Stack space={["xlarge", "xxlarge"]}>
            <Stack space={["medium", "large"]}>
              <Stack>
                <Heading variant="h3">{content.heading}</Heading>
                <Divider />
                <Text>{content.subHeading}</Text>
              </Stack>
              <TextField
                data-id="first-name"
                id="first-name-input"
                label={content.firstNameLabel}
                error={!!firstNameError}
                helperText={firstNameError}
                onChange={(event) => {
                  setFirstNameError(
                    validateName(event.target.value) || undefined
                  );
                  setFirstName(event.target.value);
                }}
              />
              <TextField
                data-id="last-name"
                id="last-name-input"
                label={content.lastNameLabel}
                error={!!lastNameError}
                helperText={lastNameError}
                onChange={(event) => {
                  setLastNameError(
                    validateName(event.target.value) || undefined
                  );
                  setLastName(event.target.value);
                }}
              />
              <TextField
                data-id="phone"
                id="phone-input"
                label={content.phoneNumberLabel}
                error={!!phoneError}
                helperText={phoneError}
                onChange={(event) => {
                  setPhone(event.target.value);
                }}
                onBlur={(event) => {
                  setPhoneError(validatePhone(event.target.value) || undefined);
                  setPhone(event.target.value);
                }}
              />
              <TextField
                data-id="email"
                id="email-input"
                label={content.emailLabel}
                error={!!emailError}
                helperText={emailError}
                onChange={(event) => {
                  setEmail(event.target.value);
                }}
                onBlur={(event) => {
                  setEmailError(validateEmail(event.target.value) || undefined);
                  setEmail(event.target.value);
                }}
              />
            </Stack>
            <Stack>
              <Button
                data-id="submit-callback"
                variant="contained"
                type="submit"
                spinner={submittingForm}
              >
                {content.formSubmitButton}
              </Button>
              <Text variant="body-small">
                <MarkdownLite>{content.privacyPolicy}</MarkdownLite>
              </Text>
            </Stack>
            <CaptchaDisclaimer />
          </Stack>
        </Card>
      </form>
    </Stack>
  );
};
